import { Box, IconButton, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as RefreshIcon } from "../../../assets/icons/refresh.svg";
import { ReactComponent as FilterIcon } from "../../../assets/icons/sort.svg";
import { ReactComponent as AddIcon } from "../../../assets/Images/AddIcon.svg";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ReactComponent as SuccessIcon } from "../../../assets/Images/SuccessProjectIcon.svg";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import { dashboardStyles } from "../../../assets/MUIStyles/dashboard";
import BaseButton from "../Common/Buttons/BaseButton";
import { PAGE_SIZE_50 } from "../CommonComponents/Common";
import CommonModal from "../CommonComponents/Modal/CommonModal";
import CommonPopup from "../CommonComponents/PopUpCards/CommonPopup";
import DeletePopup from "../CommonComponents/PopUpCards/DeletePopUp";
import {
  deleteLeadsAction,
  getLeadsCountAction,
  getLeadsDataAction,
  getProjectsAction,
} from "../Projects/Store/projects.action";
import LeadFilterBody from "./LeadFilterBody";
import LeadsTable from "./LeadsTable";
import SelectWorkflowModal from "./SelectWorkflowModal";
import { getWorkflowsAction } from "./Store/workflows.action";
import WorkflowModal from "./WorkflowModal";
import SearchBar from "../Common/Search/SearchBar";
import useDebounce from "../../Hooks/useDebounce";

const Leads = () => {
  const dispatch = useDispatch();
  const reducer = useSelector((reducer: any) => reducer);
  const {
    ProjectReducer: { leadsList },
  } = reducer;
  const [workflowModal, setWorkflowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [leadsState, setLeadsState] = useState({ pageIndex: 1 });
  const [selectWorkflowModal, setSelectWorkflowModal] = useState(false);
  const [selectedContactsData, setSelectedContactsData] = useState<
    { firstName: string; lastName: string; phone: string }[]
  >([]);
  const [workflows, setWorkflows] = useState([]);
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const [confirmationPopUp, setConfirmationPopUp] = useState(false);
  const [deletePopUp, setDeletePopUp] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [filterPopup, setFilterPopup] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filterState, setFilterState] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    tags: [],
    createdAt: {
      from: "",
      to: "",
    },
    updatedAt: {
      from: "",
      to: "",
    },
  });
  const [searchTerm, setSearchTerm] = useState("");
  const { pageIndex } = leadsState;
  const debouncedSearchTerm = useDebounce(searchTerm, 3000);
  const [refreshTrigger, setRefreshTrigger] = useState(false);

  useEffect(() => {
    const init = async () => {
      await dispatch(getProjectsAction());
      const res: any = await dispatch(getWorkflowsAction());
      const workflowData = res
        ?.map((item) => {
          if (item?.type === "workflow") {
            return item;
          }
        })
        ?.filter(Boolean);

      const workflowLists = workflowData?.map((wf) => ({
        id: wf._id,
        name: wf?.workflows?.name,
      }));

      if (workflowLists?.length > 0) {
        setWorkflows(workflowLists);
      }
    };

    init();
  }, []);

  useEffect(() => {
    fetchLeadsData(!(leadsList?.length > 0));
  }, [pageIndex, searchText, refreshTrigger]);

  useEffect(() => {
    setSearchText(debouncedSearchTerm);
    setLeadsState((prev) => ({
      ...prev,
      pageIndex: 1,
    }));
  }, [debouncedSearchTerm]);

  const fetchLeadsData = async (loading = true) => {
    setLoading(loading);
    const params = {
      pageIndex: pageIndex,
      pageSize: PAGE_SIZE_50,
      searchtext: searchText,
    };
    await dispatch(getLeadsDataAction(params));
    await dispatch(getLeadsCountAction({ ...params, all: "true" }));

    setLoading(false);
    setRefreshLoading(false);
  };

  const refreshData = async () => {
    setRefreshLoading(true);
    await fetchLeadsData(false);
  };

  const leadsObj = {
    workflows,
    setLeadsState,
    loading,
    selectedContactsData,
    setSelectedContactsData,
    selectedRows,
    setSelectedRows,
    setFilterState,
    leadsState,
    searchText,
  };

  const contactObj = {
    selectWorkflowModal,
    workflows,
    setSelectWorkflowModal,
    selectedContactsData,
    setSelectedContactsData,
    setSelectedRows,
    dispatch,
    fetchLeadsData,
    filterState,
    setFilterState,
  };

  const leadFilterObj = { filterState, setFilterState };

  const isFilterApplied = Object.entries(filterState)?.some(
    ([key, value]: any) => {
      if (typeof value === "string") {
        return value.trim() !== "";
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (
        typeof value === "object" &&
        value?.from !== undefined &&
        value?.to !== undefined
      ) {
        return Boolean(value.from) || Boolean(value.to);
      }
      return false;
    }
  );

  const isDisabled = !(selectedRows?.length > 0 || isFilterApplied);

  const handleToggle = () => {
    setFilterPopup((prev) => !prev);
    setFilterState({
      firstName: "",
      lastName: "",
      phone: "",
      tags: [],
      createdAt: {
        from: "",
        to: "",
      },
      updatedAt: {
        from: "",
        to: "",
      },
    });
  };

  const handleBulkDelete = async () => {
    setDeleteLoading(true);
    const res: any = await dispatch(deleteLeadsAction(selectedRows));
    if (res?.status === 200) {
      setSelectedRows([]);
      setDeleteLoading(false);
      setDeletePopUp(true);
      setConfirmationPopUp(false);
      setLeadsState({ pageIndex: 1 });
      setRefreshTrigger((prev) => !prev);
    }
  };

  return (
    <Box sx={{ py: 1 }}>
      <Box mb={1.5} className="flex-between gap-2">
        <Typography className="font-20-grey">Leads</Typography>
        <Box className="flex-only gap-2">
          <SearchBar
            searchText={searchTerm}
            handleChange={(e) => setSearchTerm(e.target.value)}
          />
          <BaseButton
            variant="contained"
            title="Delete Leads"
            startIcon={
              <DeleteOutlineIcon fontSize="small" sx={{ padding: 0 }} />
            }
            disabled={selectedRows.length === 0}
            handleClick={() => setConfirmationPopUp(true)}
          />
          <BaseButton
            variant="contained"
            title="Attach Workflow"
            startIcon={<AddIcon />}
            disabled={isDisabled}
            handleClick={() => setSelectWorkflowModal(true)}
          />
          <BaseButton
            variant="contained"
            title="Add Contacts"
            startIcon={<AddIcon />}
            handleClick={() => setWorkflowModal(true)}
          />

          <IconButton
            title="Apply Filters"
            onClick={() => setFilterPopup(true)}
            sx={commonStyles.filterButtonStyle}
          >
            <FilterIcon />
          </IconButton>

          <IconButton
            title="Refresh"
            onClick={refreshData}
            sx={{
              ...commonStyles.refreshButtonStyle,
              ...dashboardStyles.refreshAnimation(refreshLoading),
            }}
            disabled={refreshLoading}
          >
            <RefreshIcon className="refresh-icon" height={14} width={14} />
          </IconButton>
        </Box>
      </Box>

      <LeadsTable leadsObj={leadsObj} />

      {workflowModal && (
        <WorkflowModal
          workflowModal={workflowModal}
          setWorkflowModal={setWorkflowModal}
          fetchLeadsData={fetchLeadsData}
        />
      )}

      {selectWorkflowModal && <SelectWorkflowModal contactObj={contactObj} />}

      {filterPopup && (
        <CommonModal
          body={<LeadFilterBody leadFilterObj={leadFilterObj} />}
          handleSave={() => {
            setFilterPopup(false);
            setSelectWorkflowModal(true);
          }}
          heading={`Add Lead Filters`}
          show={filterPopup}
          size={600}
          toggle={handleToggle}
          saveButtonText="Select Workflow"
          spin={false}
          disableSaveButton={!isFilterApplied}
        />
      )}

      {deletePopUp && (
        <CommonPopup
          message={`Selected leads removed successfully`}
          show={deletePopUp}
          toggle={() => setDeletePopUp((prev) => !prev)}
          Icon={SuccessIcon}
          heading={`Leads Removed`}
        />
      )}

      {confirmationPopUp && (
        <DeletePopup
          show={confirmationPopUp}
          toggle={() => setConfirmationPopUp((prev) => !prev)}
          message={`Are you sure you want to delete ${selectedRows.length} selected leads?`}
          handleDelete={handleBulkDelete}
          spin={deleteLoading}
        />
      )}
    </Box>
  );
};

export default Leads;
